'use client';

/**
 * LandingHeader.tsx — topplinje for den offentlige forsiden
 *
 * Logo (samme blå boks + hvit logo som UI Shell), anker-navigasjon, språkbryter
 * (NO | EN), temabryter og «Logg inn». Navigasjonen skjules under lg for å gi
 * plass; språk, tema og innlogging er alltid synlige.
 */

import Link from 'next/link';
import { Login, Moon, Sun } from '@carbon/icons-react';
import type { LandingContent, Locale } from '@/types/landing';
import { useTheme } from '@/hooks/useTheme';
import styles from './landing-header.module.css';

interface LandingHeaderProps {
  ui: LandingContent['ui'];
  nav: LandingContent['nav'];
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
}

const LOCALES: { value: Locale; label: string; name: string }[] = [
  { value: 'no', label: 'NO', name: 'Norsk' },
  { value: 'en', label: 'EN', name: 'English' },
];

export default function LandingHeader({ ui, nav, locale, onLocaleChange }: LandingHeaderProps) {
  const { theme, toggle } = useTheme();
  const ThemeIcon = theme === 'dark' ? Sun : Moon;

  return (
    <header className={styles.header}>
      <a href="#innhold" className={styles.skip}>
        {ui.skipToContent}
      </a>

      <div className={styles.inner}>
        <Link href="/" className={styles.brand} aria-label="inSuite">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/Logo/inSuiteWhiteFontSVG.png" alt="" className={styles.logo} />
        </Link>

        <nav className={styles.nav} aria-label={ui.navLabel}>
          <ul className={styles.navList}>
            {nav.map((item) => (
              <li key={item.href}>
                {item.href.startsWith('#') ? (
                  <a href={item.href} className={styles.navLink}>
                    {item.label}
                  </a>
                ) : (
                  <Link href={item.href} className={styles.navLink}>
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.actions}>
          <div className={styles.segment} role="group" aria-label={ui.languageLabel}>
            {LOCALES.map((l) => (
              <button
                key={l.value}
                type="button"
                lang={l.value}
                className={styles.segmentButton}
                aria-pressed={locale === l.value}
                aria-label={l.name}
                onClick={() => onLocaleChange(l.value)}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            className={styles.iconButton}
            onClick={toggle}
            aria-label={theme === 'dark' ? ui.themeToLight : ui.themeToDark}
            title={ui.themeLabel}
          >
            <ThemeIcon size={20} aria-hidden />
          </button>

          <Link href="/login" className={styles.login} aria-label={ui.login}>
            <span className={styles.loginLabel} aria-hidden>
              {ui.login}
            </span>
            <Login size={20} aria-hidden />
          </Link>
        </div>
      </div>
    </header>
  );
}
